import React from "react";
import { useLocalization } from "../src/hooks/useLocaleFormatter";
import { useTranslate } from "../src/hooks/useTranslate";
import { ProductRackFragment } from "../src/model/fragments";
import { getProductRackLink } from "../src/model/urls";
import { FlexiLink } from "./FlexiLink";
import { Picture } from "./Picture";
import { ConditionalWrap } from "./ConditionalWrap";
import classNames from "classnames";

export type ProductRackListProps = {
    racks: ProductRackFragment[],
    mode: 'vendors' | 'products'
}

export const ProductRackList: React.FunctionComponent<ProductRackListProps> = ({
    racks,
    mode
}) => {
    const localization = useLocalization();
    const translate = useTranslate();

    // in product mode, display only the newest racks of a given bind ID
    const applicable = React.useMemo(() => {
        if(mode == 'products')
        {
            return racks;
        }

        const map = new Map<string, ProductRackFragment>();
        for(const rack of racks)
        {
            if(!map.has(rack.bindId))
            {
                map.set(rack.bindId, rack);
            }
            else if(map.get(rack.bindId)!.year < rack.year)
            {
                map.set(rack.bindId, {
                    ...rack,
                    vendorCount: map.get(rack.bindId)!.vendorCount + rack.vendorCount,
                    variantCount: map.get(rack.bindId)!.variantCount + rack.variantCount,
                });
            }
            else 
            {
                map.set(rack.bindId, {
                    ...map.get(rack.bindId)!,
                    vendorCount: map.get(rack.bindId)!.vendorCount + rack.vendorCount,
                    variantCount: map.get(rack.bindId)!.variantCount + rack.variantCount,
                });
            }
        }

        const result = Array.from(map.values());
        return result;
    }, [racks, mode]);

    return <div className='productRackList'>
        {applicable.map((rack, i) => {
            const data = localization.extractLocale(rack.locales);
            if(!data || !data.link)
            {
                return null;
            }

            return <React.Fragment key={rack.id}>
                <ConditionalWrap
                    condition={mode != 'vendors'}
                    wrap={children => <FlexiLink nextLink={
                        getProductRackLink(
                            localization.locale,
                            rack.id,
                            data.link.url,
                        )
                    }>{children}</FlexiLink>}
                >
                    <a className={classNames("productRackList-item", mode != 'vendors' && 'productRackList-item--active')}>
                        <div className="productRackList-item-header">
                            
                            <h6>{data.title}</h6>
                        </div>
                        <div className="productRackList-item-flex">
                            <div>
                                {rack.image && (
                                    <Picture
                                        alt={data?.title}
                                        image={rack.image}
                                        width={80}
                                        height={80}
                                        mode={'cover'}
                                    />
                                )}
                            </div>
                            <div className="productRackList-item-info">
                                <small>{ translate('productRack') } { rack.bindId }</small>
                                
                                {
                                    mode == 'vendors' && 
                                    <span>{rack.vendorCount} { translate(rack.vendorCount == 1 ? 'productRacks-location' : 'productRacks-locations') }</span>
                                }
                                {
                                    mode == 'products' && <>
                                    <span>{rack.variantCount} { translate(rack.variantCount <= 3 ? 'productRacks-product' : 'productRacks-products') }</span>
                                    </>
                                }
                            </div>
                        </div>
                    </a>
                </ConditionalWrap>
            </React.Fragment>
        })}
    </div>;
}
